import {Fab, MenuItem} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import {changeBeneficiaryDetails, fetchBeneficiaryDetails} from 'Beneficiaries/ducks/beneficiaryDetail';
import {ContingentBeneficiary, PrimaryBeneficiary} from 'Beneficiaries/models/PersonTypes';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {ModalState, showModal} from 'Core/ducks/modal';
import {Empty, fromResult} from 'Core/models/Async';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import {maskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './Beneficiaries.sass';

const BeneficiaryDetail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {policyNumber} = useParams();
  const {beneficiaryDetails} = useSelector((state: ReducerState) => state.beneficiaryDetail);
  const scopes = useSelector((state: ReducerState) => selectUserScopes(state));

  useEffect(() => {
    if (!policyNumber) {
      history.push(BeneficiariesRouteConstants.BENEFICIARIES);
    } else {
      dispatch(fetchBeneficiaryDetails(policyNumber));
    }

    return () => {
      dispatch(changeBeneficiaryDetails(new Empty()));
    };
  }, [policyNumber]);

  // If person is of type beneficiary, then allow add or delete, as well as all editing
  const isBeneficiary = (personTypeId?: number) =>
    personTypeId === PrimaryBeneficiary.id || personTypeId === ContingentBeneficiary.id;

  return renderAsyncView(beneficiaryDetails, result => (
    <>
      <PagedView
        pagedViewKey="beneficiaryDetail"
        loadMore={() => {}}
        data={fromResult({
          currentPageNumber: 0,
          links: [],
          result,
          totalCount: result.length,
        })}
      >
        {columnProps => (
          <>
            <Column {...columnProps} title="Policy Number" default>
              {() => policyNumber!}
            </Column>
            <Column {...columnProps} title="First Name" default>
              {beneficiaryPerson => beneficiaryPerson.firstName}
            </Column>
            <Column {...columnProps} title="Middle Name" default>
              {beneficiaryPerson => beneficiaryPerson.middleName}
            </Column>
            <Column {...columnProps} title="Last Name" default>
              {beneficiaryPerson => beneficiaryPerson.lastName}
            </Column>
            <Column {...columnProps} title="SSN" default>
              {beneficiaryPerson => beneficiaryPerson.identifier?.value}
            </Column>
            <Column {...columnProps} title="Type" default>
              {beneficiaryPerson => beneficiaryPerson.personType?.name}
            </Column>
            <Column {...columnProps} title="Percentage" default>
              {beneficiaryPerson =>
                beneficiaryPerson.personType?.id === PrimaryBeneficiary.id ||
                beneficiaryPerson.personType?.id === ContingentBeneficiary.id
                  ? `${beneficiaryPerson.beneficiaryPercent || 0}%`
                  : 'N/A'
              }
            </Column>
            <Column {...columnProps} title="SIgnature Date" default>
              {beneficiaryPerson => {
                const shouldUseDefaultDate =
                  !beneficiaryPerson.signatureDate || beneficiaryPerson.signatureDate.valueOf().toString() === "978287400000";

                return toFormattedDate(`${shouldUseDefaultDate ? '01-01-1900' : beneficiaryPerson.signatureDate}`);
              }}
            </Column>
            <Column {...columnProps} title="Relationship">
              {beneficiaryPerson => beneficiaryPerson.relationship}
            </Column>
            <Column {...columnProps} title="Date of Birth">
              {beneficiaryPerson => toFormattedDate(beneficiaryPerson.dateOfBirth?.value)}
            </Column>
            <Column {...columnProps} title="Address">
              {beneficiaryPerson =>
                `${beneficiaryPerson.address1 || ''} ${beneficiaryPerson.address2 || ''} ${beneficiaryPerson.address3 ||
                  ''} ${beneficiaryPerson.city || ''} ${beneficiaryPerson.state || ''} ${beneficiaryPerson.zipcode ||
                  ''}`
              }
            </Column>
            <Column {...columnProps} title="Gender">
              {beneficiaryPerson => beneficiaryPerson.gender}
            </Column>
            <Column {...columnProps} title="Email">
              {beneficiaryPerson => beneficiaryPerson.email?.address}
            </Column>
            <Column {...columnProps} title="Phone Number">
              {beneficiaryPerson => maskPhoneNumber(beneficiaryPerson.phoneNumber?.phoneNumber)}
            </Column>
            <Column
              {...columnProps}
              isActions
              title=""
              default
              visible={() => [Scope.BeneficiariesDelete, Scope.BeneficiariesEdit].some(x => scopes.includes(x))}
            >
              {beneficiaryPerson => (
                <ActionsMenu>
                  {close => (
                    <RequireScope scopes={scopes} scope={Scope.BeneficiariesEdit}>
                      <>
                        <Link to={BeneficiariesRouteConstants.BENEFICIARY_UPDATE(beneficiaryPerson.id!, policyNumber!)}>
                          <MenuItem>Update</MenuItem>
                        </Link>
                        <MenuItem
                          onClick={() => {
                            dispatch(
                              showModal(
                                ModalState.DeleteBeneficiaryConfirmation({
                                  policyNumber: policyNumber!,
                                  id: beneficiaryPerson.id!,
                                })
                              )
                            );
                            close();
                          }}
                          disabled={!isBeneficiary(beneficiaryPerson.personType?.id)}
                        >
                          Delete Beneficiary
                        </MenuItem>
                      </>
                    </RequireScope>
                  )}
                </ActionsMenu>
              )}
            </Column>
          </>
        )}
      </PagedView>
      {beneficiaryDetails && (
        <RequireScope scopes={scopes} scope={Scope.BeneficiariesAdd}>
          <Fab
            className="beneficiaries__add-new-fab"
            color="primary"
            variant="extended"
            onClick={() => history.push(BeneficiariesRouteConstants.BENEFICIARY_PERSON_ADD(policyNumber!))}
          >
            <AddIcon className="beneficiaries__extended-icon" aria-label="add" />
            Add
          </Fab>
        </RequireScope>
      )}
    </>
  ));
};

export default BeneficiaryDetail;
